import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { selectedBlogBuilderAccess } from '../utils/documentAccess';

const AuthContext = createContext(null);

// Session warning shows 5 minutes before access token expiry
const WARNING_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [sessionExpiryWarning, setSessionExpiryWarning] = useState(false);

  const expiresAtRef = useRef(null);
  const warningTimerRef = useRef(null);

  useEffect(() => {
    checkAuth();
    return () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, []);

  // ── Refresh token helpers (sessionStorage — survives reloads, cleared when tab closes) ──
  const getRefreshToken = () => sessionStorage.getItem('refreshToken');
  const setRefreshToken = (token) => {
    if (token) {
      sessionStorage.setItem('refreshToken', token);
    } else {
      sessionStorage.removeItem('refreshToken');
    }
  };

  // ── Start session expiry timer ──
  const startSessionTimer = useCallback((expiresAtUnix) => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);

    expiresAtRef.current = expiresAtUnix;
    const msUntilExpiry = (expiresAtUnix * 1000) - Date.now();
    const msUntilWarning = msUntilExpiry - WARNING_BEFORE_EXPIRY_MS;

    if (msUntilWarning > 0) {
      warningTimerRef.current = setTimeout(() => {
        setSessionExpiryWarning(true);
      }, msUntilWarning);
    } else if (msUntilExpiry > 0) {
      // Less than 5 min left, show warning immediately
      setSessionExpiryWarning(true);
    }
  }, []);

  // ── Check existing auth on mount ──
  const checkAuth = async () => {
    // Skip auth check on SSO callback — that page handles its own auth flow
    if (window.location.pathname === '/auth/sso') {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);

      // Restore auth_source to localStorage for 401 interceptor
      if (response.data.auth_source) {
        localStorage.setItem('auth_source', response.data.auth_source);
      }

      // Set default blog
      if (response.data.blogs && response.data.blogs.length > 0) {
        const savedBlogId = localStorage.getItem('selectedBlog');
        const blog = response.data.blogs.find(b => b.id === parseInt(savedBlogId)) || response.data.blogs[0];
        setSelectedBlog(blog);
      }

      // Restore session timer from saved expiresAt if available
      const savedExpiresAt = localStorage.getItem('expiresAt');
      if (savedExpiresAt) {
        startSessionTimer(parseInt(savedExpiresAt));
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('auth_source');
      localStorage.removeItem('expiresAt');
    } finally {
      setLoading(false);
    }
  };

  // ── SSO login (called from SSOCallback page) ──
  const loginFromSSO = async (responseData) => {
    const { token, refreshToken, expiresAt, user: userData, selectedBlogId } = responseData;

    localStorage.setItem('token', token);
    localStorage.setItem('auth_source', userData.auth_source || 'wordpress');
    localStorage.setItem('expiresAt', String(expiresAt));
    setRefreshToken(refreshToken);

    setUser(userData);

    // Set the blog that was included in the SSO token
    if (userData.blogs && userData.blogs.length > 0) {
      const blog = userData.blogs.find(b => b.id === selectedBlogId) || userData.blogs[0];
      setSelectedBlog(blog);
      localStorage.setItem('selectedBlog', blog.id);
    }

    // Start session expiry timer
    startSessionTimer(expiresAt);
  };

  // ── Refresh session (called when user clicks "Yes, stay signed in") ──
  const refreshSession = async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/sso/refresh', { refreshToken });
    const { token, refreshToken: newRefreshToken, expiresAt } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('expiresAt', String(expiresAt));
    setRefreshToken(newRefreshToken);

    // Dismiss the warning modal and restart timer
    setSessionExpiryWarning(false);
    startSessionTimer(expiresAt);
    await checkAuth();
  };

  // ── Logout ──
  const logout = async () => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('auth_source');
      localStorage.removeItem('expiresAt');
      localStorage.removeItem('returnUrl');
      setRefreshToken(null);
      expiresAtRef.current = null;
      setUser(null);
      setSelectedBlog(null);
      setSessionExpiryWarning(false);
    }
  };

  const switchBlog = (blog) => {
    setSelectedBlog(blog);
    localStorage.setItem('selectedBlog', blog.id);
  };

  const updatePreferences = async (preferences) => {
    const response = await api.put('/preferences', preferences);
    setUser(prev => ({
      ...prev,
      preferences: response.data
    }));
    return response.data;
  };

  const accessLevel = selectedBlogBuilderAccess(user, selectedBlog);
  const value = {
    canAccessBuilder: accessLevel !== 'none',
    customerDocsOnly: accessLevel === 'customer',
    user,
    loading,
    selectedBlog,
    sessionExpiryWarning,
    loginFromSSO,
    refreshSession,
    logout,
    switchBlog,
    updatePreferences,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
